import React from "react";
import {
    ClipboardDocumentListIcon,
    StarIcon,
    ChartBarIcon,
    DocumentTextIcon,
    PlusIcon,
    EyeIcon,
    CloudArrowUpIcon,
} from "@heroicons/react/24/outline";
import { DataTable } from "../../common/DataTable";

const PerformanceTab = ({
    dashboardData,
    setModalMode,
    resetPerformanceForm,
    setShowPerformanceModal,
    performanceRecords,
    setSelectedRecord,
    setBgvForm,
    navigate,
    setPerformanceUploadFile,
    performancePagination,
    loadPerformanceUsers,
    loading,
    canUploadFiles = false, // customer admins and security analysts only
}) => {
    const columns = [
        { accessorKey: "firstname", header: "Employee" },
        { accessorKey: "department", header: "Department" },
        { accessorKey: "DOJ", header: "DOJ" },
        {
            id: "status",
            header: "Performance Status",
            cell: () => <span>Good</span>,
        },
        { accessorKey: "reporting_to", header: "Reporting To" },
        {
            id: "actions",
            header: "Actions",
            enableSorting: false,
            cell: ({ row }) => {
                const record = row.original;
                return (
                    <div className="flex items-left justify-left space-x-2">
                        <button
                            onClick={() => {
                                setSelectedRecord(record);
                                setBgvForm(record);
                                setModalMode("edit");
                                navigate("/documents", {
                                    state: { openTab: "performance_record", selectedRecord: record },
                                });
                            }}
                            className="text-blue-600 hover:text-blue-900"
                        >
                            <EyeIcon className="w-4 h-4 mr-1" />
                        </button>
                        {canUploadFiles && (
                            <button
                                onClick={() => {
                                    setSelectedRecord(record);
                                    setPerformanceUploadFile(true);
                                }}
                                className="text-blue-600 hover:text-blue-900"
                                title="Upload performance document"
                            >
                                <CloudArrowUpIcon className="w-4 h-4 mr-1" />
                            </button>
                        )}
                    </div>
                );
            },
        },
    ];

    return (
    <div className="space-y-6">
        {dashboardData && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center">
                        <ClipboardDocumentListIcon className="w-8 h-8 text-blue-600" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Total Employees</p>
                            <p className="text-2xl font-bold text-gray-900">{dashboardData.performance_summary?.total_employees || 0}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center">
                        <StarIcon className="w-8 h-8 text-yellow-600" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Excellent Rating</p>
                            <p className="text-2xl font-bold text-gray-900">{dashboardData.performance_summary?.excellent_rating || 0}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center">
                        <ChartBarIcon className="w-8 h-8 text-green-600" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Avg KPI Score</p>
                            <p className="text-2xl font-bold text-gray-900">{dashboardData.performance_summary?.average_kpi || 0}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                    <div className="flex items-center">
                        <DocumentTextIcon className="w-8 h-8 text-purple-600" />
                        <div className="ml-4">
                            <p className="text-sm font-medium text-gray-600">Excellence Rate</p>
                            <p className="text-2xl font-bold text-gray-900">{dashboardData.performance_summary?.excellence_rate || 0}%</p>
                        </div>
                    </div>
                </div>
            </div>
        )}

        <div className="flex justify-end">
            <button
                onClick={() => {
                    setModalMode("create");
                    resetPerformanceForm();
                    setShowPerformanceModal(true);
                }}
                className="inline-flex items-center px-4 py-2 bg-blue-600 border border-transparent rounded-md font-medium text-white hover:bg-blue-700"
            >
                <PlusIcon className="w-5 h-5 mr-2" />
                Add Performance Record
            </button>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Performance Records</h3>
                <p className="text-sm text-gray-600 mt-1">Track employee KPI/KRI, goals, and ratings</p>
            </div>

            <DataTable
                columns={columns}
                data={performanceRecords}
                getRowId={(record) => String(record.employee_id)}
                loading={loading}
                emptyMessage="No performance records found"
                manualPagination
                rowCount={performancePagination.totalCount || 0}
                pagination={{ pageIndex: (performancePagination.currentPage || 1) - 1, pageSize: performancePagination.pageSize }}
                onPaginationChange={(updater) => {
                    const current = { pageIndex: (performancePagination.currentPage || 1) - 1, pageSize: performancePagination.pageSize };
                    const next = typeof updater === "function" ? updater(current) : updater;
                    loadPerformanceUsers(next.pageIndex + 1, next.pageSize);
                }}
                enableColumnVisibility={false}
                enableColumnResizing
                maxBodyHeight="65vh"
            />
        </div>
    </div>
    );
};

export default PerformanceTab;
